import type { RouteId, ScheduledDeparture } from '../state/events';
import {
  CALENDARS,
  EXCEPTIONS,
  STOPS_44_0,
  STOPS_44_1,
  STOPS_E_0,
  STOPS_E_1,
  TRIPS_44_0,
  TRIPS_44_1,
  TRIPS_E_0,
  TRIPS_E_1,
} from './timetable-data';

export const SCHEDULE_VALID_FROM = '2026-09-14';
export const SCHEDULE_VALID_TO = '2027-03-26';

export const ROUTES: { id: RouteId; name: string }[] = [
  { id: 'E', name: 'RapidRide E Line' },
  { id: '44', name: 'Route 44' },
];

const DIRECTIONS: Record<RouteId, { id: string; label: string }[]> = {
  E: [
    { id: '0', label: 'Aurora Village Transit Center' },
    { id: '1', label: 'Downtown Seattle' },
  ],
  44: [
    { id: '0', label: 'Ballard Wallingford' },
    { id: '1', label: 'University District / UW Medical Center Wallingford' },
  ],
};

type Stop = { id: string; name: string };
type Trip = [tripId: string, serviceId: string, destination: string, times: number[]];
type DirectionData = { stops: [string, string][]; trips: Trip[] };

const DATA: Record<RouteId, Record<string, DirectionData>> = {
  E: {
    '0': { stops: STOPS_E_0, trips: TRIPS_E_0 },
    '1': { stops: STOPS_E_1, trips: TRIPS_E_1 },
  },
  44: {
    '0': { stops: STOPS_44_0, trips: TRIPS_44_0 },
    '1': { stops: STOPS_44_1, trips: TRIPS_44_1 },
  },
};

const SEATTLE_TIME_ZONE = 'America/Los_Angeles';
const LOCAL_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: SEATTLE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function partsOf(date: Date) {
  return Object.fromEntries(LOCAL_PARTS.formatToParts(date).map((part) => [part.type, part.value]));
}

function localDateKey(date: Date): string {
  const parts = partsOf(date);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function dateKeyOffset(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function weekday(dateKey: string): number {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function serviceRuns(serviceId: string, dateKey: string): boolean {
  const compactDate = dateKey.replaceAll('-', '');
  const exception = EXCEPTIONS.find(
    (candidate) => candidate.service_id === serviceId && candidate.date === compactDate,
  );
  if (exception) return exception.type === 1;
  const calendar = CALENDARS.find((candidate) => candidate.service_id === serviceId);
  if (!calendar || compactDate < calendar.start || compactDate > calendar.end) return false;
  return calendar.weekdays[(weekday(dateKey) + 6) % 7] === 1;
}

function localDateTimeToUtc(dateKey: string, minutes: number): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  const wallClock = Date.UTC(year, month - 1, day, Math.floor(minutes / 60), minutes % 60);
  let guess = wallClock;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = partsOf(new Date(guess));
    const displayed = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
    );
    guess += wallClock - displayed;
  }

  return new Date(guess);
}

function directionData(routeId: RouteId, directionId: string): DirectionData | undefined {
  return DATA[routeId][directionId];
}

export function getDirections(routeId: RouteId): { id: string; label: string }[] {
  return DIRECTIONS[routeId] ?? [];
}

export function getStops(routeId: RouteId, directionId: string): Stop[] {
  return directionData(routeId, directionId)?.stops.map(([id, name]) => ({ id, name })) ?? [];
}

export function getNextDepartures(
  routeId: RouteId,
  directionId: string,
  stopId: string,
  at: Date,
): ScheduledDeparture[] {
  const selected = directionData(routeId, directionId);
  if (!selected || Number.isNaN(at.getTime())) return [];

  const stopIndex = selected.stops.findIndex(([id]) => id === stopId);
  if (stopIndex < 0) return [];

  const serviceDate = localDateKey(at);
  const departures: ScheduledDeparture[] = [];

  for (let dayOffset = -2; dayOffset <= 2; dayOffset += 1) {
    const operatingDate = dateKeyOffset(serviceDate, dayOffset);
    for (const [tripId, serviceId, destination, times] of selected.trips) {
      if (!serviceRuns(serviceId, operatingDate)) continue;
      const minutes = times[stopIndex];
      if (minutes === undefined) continue;

      const departure = localDateTimeToUtc(operatingDate, minutes);
      const difference = departure.getTime() - at.getTime();
      if (difference <= 0) continue;
      departures.push({
        time: departure.toISOString(),
        destination,
        minutesAway: Math.ceil(difference / 60000),
        tripId,
      });
    }
  }

  return departures.sort((first, second) => first.time.localeCompare(second.time)).slice(0, 3);
}

export function isScheduleAvailable(at: Date): boolean {
  if (Number.isNaN(at.getTime())) return false;
  const date = localDateKey(at);
  if (date < SCHEDULE_VALID_FROM || date > SCHEDULE_VALID_TO) return false;
  return CALENDARS.some(
    (calendar) => serviceRuns(calendar.service_id, date) || serviceRuns(calendar.service_id, dateKeyOffset(date, -1)),
  );
}

export const SCHEDULE_SOURCE =
  'King County Metro official GTFS: https://metro.kingcounty.gov/GTFS/google_transit.zip; downloaded 2026-09-26 (feed Last-Modified 2026-09-14).';
